import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SummaryItems } from 'fc-ui-components';
import { UserService } from 'services/user.service';
import { LanguageService } from 'services/language.service';
import {
    calculateTotalAmountMapPerArticle,
    flattenMultiStoreArticle,
    getTotalPriceByItem
} from 'app/b2b-order/helpers/order';
import { formatPrice } from 'app/b2b-order/helpers/price';
import { PreorderArticle } from 'app/b2b-order/types/preorder';
import { ReorderArticle } from 'app/b2b-order/types/reorder';

@Injectable()
export class ProductDetailsSummaryService {
    private readonly emptyValue = '--';

    constructor(
        private userService: UserService,
        private languageService: LanguageService
    ) {}

    public productSummary(articles: ReorderArticle[]): Observable<SummaryItems> {
        const summaryPerArticle = this.getSummaryPerArticle(articles);
        const summary = this.getSummary(summaryPerArticle);

        return combineLatest([
            this.languageService.translateObservable('ORDER.PRODUCT_DETAILS.TOTAL_ARTICLES'),
            this.languageService.translateObservable('ORDER.PRODUCT_DETAILS.TOTAL_ITEMS'),
            this.languageService.translateObservable('ORDER.PRODUCT_DETAILS.TOTAL_PP')
        ]).pipe(
            map(([articlesTitle, itemsTitle, priceTitle]) => ({
                first: { title: articlesTitle, value: summary.articles },
                second: { title: itemsTitle, value: summary.items },
                third: { title: priceTitle, value: summary.price }
            }))
        );
    }

    private getSummary(articles) {
        return {
            articles: this.getTotalArticles(articles),
            items: _.reduce(articles, (total, { items }) => total + items, 0),
            price: this.getTotalPrice(articles)
        };
    }

    private getTotalArticles(articles) {
        return _.reduce(
            articles,
            (total, article) => {
                if (article.items > 0) {
                    return total + 1;
                }
                return total;
            },
            0
        );
    }

    private getSummaryPerArticle(articles) {
        return _.map(articles, (article) => {
            const normalizedArticle = flattenMultiStoreArticle(article);

            return {
                items: this.getTotalSelectedAmount(normalizedArticle),
                price: this.getPriceByItem(normalizedArticle)
            };
        });
    }

    private getTotalPrice(articles) {
        const hasSelectedItem = _.some(articles, ({ items }) => items > 0);
        const hasEmptyPrice = _.some(
            articles,
            ({ price }) => _.get(price, 'totalPrice', this.emptyValue) === this.emptyValue
        );

        if (!hasSelectedItem) {
            return '0';
        }

        const currency = articles[0].price.currency;

        if (!hasEmptyPrice && currency) {
            const price = articles.reduce((price, article) => {
                const priceValue = article.price.totalPrice;
                return price + priceValue;
            }, 0);

            return formatPrice(price, currency, this.userService.getPreferredLanguage());
        }
        return this.emptyValue;
    }

    private getTotalSelectedAmount(article: ReorderArticle | PreorderArticle) {
        const totalSelectedAmountMap = calculateTotalAmountMapPerArticle(article);
        return _.sum(_.values(totalSelectedAmountMap));
    }

    private getPriceByItem(orderArticle: ReorderArticle | PreorderArticle) {
        if (!orderArticle.price.purchasePrice) {
            return this.emptyValue;
        }

        const totalPrice = getTotalPriceByItem(orderArticle);
        return {
            totalPrice,
            currency: orderArticle.price.currency
        };
    }
}
